import type { ConnectionEvent } from "ably";
import type { InboundMessage, PresenceMessage } from "ably";
import {
  enterGlobalPresence as ablyEnterGlobalPresence,
  getAblyRealtime,
  initAblyForUser,
  isBenignChannelTransitionError,
  leaveGlobalPresence as ablyLeaveGlobalPresence,
  resetAbly,
} from "./ablyChatClient";
import {
  normalizeInboundChatMessagePayload,
} from "./chatService";
import {
  ABLY_CHAT_SETTINGS,
  CHAT_EVENTS,
  CHAT_USER_EVENT_TYPES,
  CONNECTION_EVENTS,
} from "../utils/constants";
import { logErrorDev, parseChannelData } from "../utils/formatters";

/** JSON-parse Ably payloads. */
export function normalizeAblyData(raw: unknown): unknown {
  return parseChannelData(raw);
}

export type ParsedChatUserNotification =
  | { kind: typeof CHAT_USER_EVENT_TYPES.NEW_CHAT; roomId: string | null }
  | {
      kind: typeof CHAT_USER_EVENT_TYPES.NEW_MESSAGE_ALERT;
      roomId: string | null;
    };

function pickRoomIdFromRecord(obj: Record<string, unknown>): string | null {
  const roomId = obj.roomId ?? obj.chatId;
  if (typeof roomId === "string") {
    const trimmedRoomId = roomId.trim();
    return trimmedRoomId || null;
  }
  return null;
}

/**
 * `user-events:{userId}` / `notification` — normalize type and room id across backend variants.
 */
export function parseChatUserNotificationEvent(
  raw: unknown
): ParsedChatUserNotification | null {
  const normalized = normalizeAblyData(raw);
  if (!normalized || typeof normalized !== "object") {
    return null;
  }
  const payload = normalized as Record<string, unknown>;
  const typeRaw = payload.type;
  if (typeof typeRaw !== "string" || !typeRaw.trim()) {
    return null;
  }
  const type = typeRaw.trim().toUpperCase();

  let roomId = pickRoomIdFromRecord(payload);
  if (!roomId && payload.payload != null && typeof payload.payload === "object") {
    roomId = pickRoomIdFromRecord(payload.payload as Record<string, unknown>);
  }
  if (!roomId && payload.data != null && typeof payload.data === "object") {
    roomId = pickRoomIdFromRecord(payload.data as Record<string, unknown>);
  }

  if (type === CHAT_USER_EVENT_TYPES.NEW_CHAT) {
    return { kind: CHAT_USER_EVENT_TYPES.NEW_CHAT, roomId };
  }
  if (
    type === CHAT_USER_EVENT_TYPES.NEW_MESSAGE_ALERT ||
    type === CHAT_USER_EVENT_TYPES.CHAT_MESSAGE
  ) {
    return { kind: CHAT_USER_EVENT_TYPES.NEW_MESSAGE_ALERT, roomId };
  }
  return null;
}

export function parseTypingUserId(data: unknown): string {
  if (typeof data === "string") {
    return data.trim();
  }
  if (data != null) {
    return String(data);
  }
  return "";
}

function isRoomReactionPayload(msg: InboundMessage): boolean {
  const raw = normalizeAblyData(msg.data);
  if (!raw || typeof raw !== "object") {
    return false;
  }
  const p = raw as Record<string, unknown>;
  const mid = p.messageId;
  return typeof mid === "string" && mid.toLowerCase() === "__room__";
}

function getRoomChannel(roomId: string) {
  const client = getAblyRealtime();
  if (!client) {
    throw new Error("Ably client unavailable");
  }
  return client.channels.get(roomId);
}

/**`getClient()` — Ably Realtime singleton (or null). */
export function getClient() {
  return getAblyRealtime();
}

async function runSubscribe(
  fn: () => Promise<unknown>
): Promise<void> {
  try {
    await fn();
  } catch (e) {
    if (!isBenignChannelTransitionError(e)) {
      logErrorDev("[ChatAbly] runSubscribe failed", e);
      throw e;
    }
  }
}

/**
 * Ensure Ably is initialized and the connection is ready (mobile `chatRealtimeService.connect`).
 */
export function connect(userId: string): Promise<void> {
  initAblyForUser(userId);
  const client = getAblyRealtime();
  if (!client) {
    return Promise.reject(new Error("Ably client unavailable"));
  }
  const { connection } = client;
  if (connection.state === CONNECTION_EVENTS.CONNECTED) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onConnected = () => {
      cleanup();
      resolve();
    };
    const onFailed = () => {
      cleanup();
      reject(new Error("Ably connection failed"));
    };
    function cleanup() {
      connection.off(CONNECTION_EVENTS.CONNECTED as ConnectionEvent, onConnected);
      connection.off(CONNECTION_EVENTS.FAILED as ConnectionEvent, onFailed);
    }
    connection.once(CONNECTION_EVENTS.CONNECTED as ConnectionEvent, onConnected);
    connection.once(CONNECTION_EVENTS.FAILED as ConnectionEvent, onFailed);
  });
}

/**
 * Chat messages: explicit `message` event plus unknown names that look like chat payloads.
 */
export async function subscribeRoomMessage(
  roomId: string,
  listener: (msg: InboundMessage) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();

  const onChat = (msg: InboundMessage) => {
    const ev = (msg.name || "").trim().toLowerCase();
    if (
      ev === CHAT_EVENTS.TYPING ||
      ev === CHAT_EVENTS.MESSAGE_REACTION ||
      ev === CHAT_EVENTS.ROOM_REACTION
    ) {
      return;
    }
    if (ev === CHAT_EVENTS.MESSAGE || ev === "") {
      listener(msg);
      return;
    }
    const probe = normalizeInboundChatMessagePayload(parseChannelData(msg.data));
    if (probe?.messageId) {
      listener(msg);
    }
  };

  await runSubscribe(() => channel.subscribe(onChat));
  return () => {
    channel.unsubscribe(onChat);
  };
}

export async function subscribeRoomTyping(
  roomId: string,
  listener: (msg: InboundMessage) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  const onTyping = (msg: InboundMessage) => {
    listener(msg);
  };
  await runSubscribe(() => channel.subscribe(CHAT_EVENTS.TYPING, onTyping));
  return () => {
    channel.unsubscribe(CHAT_EVENTS.TYPING, onTyping);
  };
}

export async function subscribeRoomMessageReaction(
  roomId: string,
  listener: (msg: InboundMessage) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  const onReaction = (msg: InboundMessage) => {
    if (isRoomReactionPayload(msg)) {
      return;
    }
    listener(msg);
  };
  await runSubscribe(() =>
    channel.subscribe(CHAT_EVENTS.MESSAGE_REACTION, onReaction)
  );
  return () => {
    channel.unsubscribe(CHAT_EVENTS.MESSAGE_REACTION, onReaction);
  };
}

export async function subscribeRoomReaction(
  roomId: string,
  listener: (msg: InboundMessage) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  const onNamedRoomReaction = (msg: InboundMessage) => {
    listener(msg);
  };
  await runSubscribe(() =>
    channel.subscribe(CHAT_EVENTS.ROOM_REACTION, onNamedRoomReaction)
  );
  const onMessageReactionIfRoom = (msg: InboundMessage) => {
    if (isRoomReactionPayload(msg)) {
      listener(msg);
    }
  };
  await runSubscribe(() =>
    channel.subscribe(CHAT_EVENTS.MESSAGE_REACTION, onMessageReactionIfRoom)
  );
  return () => {
    channel.unsubscribe(CHAT_EVENTS.ROOM_REACTION, onNamedRoomReaction);
    channel.unsubscribe(CHAT_EVENTS.MESSAGE_REACTION, onMessageReactionIfRoom);
  };
}

/**
 * `detachRoomChannel` — await detach after unsubscribes.
 */
export async function detachRoomChannel(roomId: string): Promise<void> {
  const client = getAblyRealtime();
  if (!client) {
    return;
  }
  const channel = client.channels.get(roomId);
  try {
    await channel.detach();
  } catch (err: unknown) {
    logErrorDev("[ChatAbly] detachRoomChannel failed", err);
  }
}

/**
 * `subscribeRoomEvent(roomId, eventName, listener)` — single named pub/sub event.
 */
export async function subscribeRoomEvent(
  roomId: string,
  eventName: string,
  listener: (msg: InboundMessage) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  const handler = (msg: InboundMessage) => {
    listener(msg);
  };
  await runSubscribe(() => channel.subscribe(eventName, handler));
  return () => {
    channel.unsubscribe(eventName, handler);
  };
}

/**
 *`enterPresence` — room-channel presence (optional; web historically avoided `leave` on rooms).
 */
export async function enterPresence(
  roomId: string,
  data: Record<string, unknown> | string = {}
): Promise<void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  await channel.presence.enter(data);
}

/**`leavePresence` — room-channel presence leave. */
export async function leavePresence(roomId: string): Promise<void> {
  const client = getAblyRealtime();
  if (!client) {
    return;
  }
  const channel = client.channels.get(roomId);
  try {
    await channel.presence.leave();
  } catch (err) {
    logErrorDev("[ChatAbly] leavePresence failed", err);
  }
}

export type RoomPresenceEvent = {
  action: string;
  clientId?: string;
  data?: unknown;
};

/** `subscribePresence` — room `presence.subscribe` with action / clientId / data. */
export async function subscribePresence(
  roomId: string,
  listener: (ev: RoomPresenceEvent) => void
): Promise<() => void> {
  const channel = getRoomChannel(roomId);
  await channel.attach();
  const handler = (m: PresenceMessage) => {
    listener({
      action: (m.action != null ? String(m.action) : "").toLowerCase(),
      clientId: m.clientId,
      data: m.data,
    });
  };
  await channel.presence.subscribe(handler);
  return () => {
    channel.presence.unsubscribe(handler);
  };
}

/**`chatRealtimeService.disconnect` — closes Realtime and clears channels. */
export function disconnect(): void {
  resetAbly();
}

/** Re-export for a single import path (`chatRealtimeService.leaveGlobalPresence`). */
export const leaveGlobalPresence = ablyLeaveGlobalPresence;

/** Re-export — call after `connect` (mobile bootstrap order). */
export const enterGlobalPresence = ablyEnterGlobalPresence;

/**`getGlobalPresenceMembers` — snapshot of online `clientId`s. */
export async function getGlobalPresenceMembers(): Promise<string[]> {
  const client = getAblyRealtime();
  if (!client) {
    return [];
  }
  const channel = client.channels.get(ABLY_CHAT_SETTINGS.GLOBAL_PRESENCE_CHANNEL);
  try {
    const members = await channel.presence.get();
    return members
      .map((m) => m.clientId)
      .filter((id): id is string => Boolean(id));
  } catch (err) {
    logErrorDev("[ChatAbly] getGlobalPresenceMembers failed", err);
    return [];
  }
}

export type GlobalPresenceEvent = {
  clientId?: string;
  action: string;
  data?: unknown;
};

/**`subscribeGlobalPresence` — unified listener (`enter` / `leave` / `present` / `update`). */
export async function subscribeGlobalPresence(
  listener: (ev: GlobalPresenceEvent) => void
): Promise<() => void> {
  const client = getAblyRealtime();
  if (!client) {
    throw new Error("Ably client unavailable");
  }
  const channel = client.channels.get(ABLY_CHAT_SETTINGS.GLOBAL_PRESENCE_CHANNEL);
  await channel.attach();
  const onPresence = (m: PresenceMessage) => {
    listener({
      clientId: m.clientId,
      action: (m.action != null ? String(m.action) : "").toLowerCase(),
      data: m.data,
    });
  };
  await channel.presence.subscribe(onPresence);
  return () => {
    channel.presence.unsubscribe(onPresence);
  };
}

/** `subscribeUserNotifications` — `user-events:{userId}` + `notification` event. */
export async function subscribeUserNotifications(
  userId: string,
  onNotification: (msg: InboundMessage) => void
): Promise<() => void> {
  const client = getAblyRealtime();
  if (!client) {
    throw new Error("Ably client unavailable");
  }
  const ch = client.channels.get(`user-events:${userId}`);
  await ch.attach();
  const handler = (msg: InboundMessage) => {
    onNotification(msg);
  };
  await runSubscribe(() => ch.subscribe(CHAT_EVENTS.NOTIFICATION, handler));
  return () => {
    ch.unsubscribe(CHAT_EVENTS.NOTIFICATION, handler);
  };
}
